import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { PiGearSix } from 'react-icons/pi';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { useCommunicationsRealtime } from 'src/modules/communications/hooks/realtime/use-communications-realtime';
import {
  COMMUNICATIONS_SPLIT_VIEW_MEDIA_QUERY,
  COMMUNICATIONS_WIDE_SPLIT_VIEW_MEDIA_QUERY,
} from 'src/modules/communications/utils/view-preference.util';
import { settingsSearchSchema } from 'src/modules/settings/schemas/settings.schema';
import Loading from 'src/modules/shared/components/atoms/loading';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useAiWorkspaceStatusIdsByStatus } from 'src/modules/shared/hooks/api/automations/employees/workspaces-ai';
import { useFunnelStatuses } from 'src/modules/shared/hooks/api/crm/funnels/funnel-statuses';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useMediaQuery } from 'src/modules/shared/hooks/use-media-query';
import { FunnelPermission } from 'src/modules/shared/types/permissions.type';
import { createQuery } from 'src/modules/shared/utils/api/query-builder.util';
import type { CommunicationsSearch } from 'src/modules/shared/schemas/communications-search.schema';
import { Route as CommunicationsRoute } from 'src/routes/communications';
import { Route as SettingsFunnelsRoute } from 'src/routes/settings/funnels';
import { useDownloadFunnelConversationTableExport } from '../../hooks/api/funnels/funnel-conversations-mutations';
import ContactDetailPanel from '../organisms/contact-detail-panel';
import { ConversationDetailPanel } from '../organisms/conversation-detail-panel';
import ConversationPanel from '../organisms/conversation-panel/conversation-panel';

const CommunicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const isSplitViewScreen = useMediaQuery(
    COMMUNICATIONS_SPLIT_VIEW_MEDIA_QUERY
  );
  const isWideSplitViewScreen = useMediaQuery(
    COMMUNICATIONS_WIDE_SPLIT_VIEW_MEDIA_QUERY
  );
  const {
    funnelId,
    conversationId,
    contactId,
    conversationExportFileId,
    conversationExportRequestId,
  } = useSearch({
    from: CommunicationsRoute.id,
    select: (search: CommunicationsSearch) => ({
      view: search.view,
      funnelId: search.funnelId,
      conversationId: search.conversationId,
      contactId: search.contactId,
      conversationExportFileId: search.conversationExportFileId,
      conversationExportRequestId: search.conversationExportRequestId,
    }),
  });
  const funnelsQuery = createQuery()
    .orderByDesc('default')
    .orderByAsc('createdAt')
    .build();
  const { funnels, loading: loadingFunnels } = useFunnels(funnelsQuery);
  const funnelStatusesQuery = useMemo(() => {
    if (!funnelId) {
      return {};
    }

    return createQuery()
      .equals('funnelId', funnelId)
      .orderByAsc('position')
      .build();
  }, [funnelId]);
  const { statuses: funnelStatuses } = useFunnelStatuses(
    funnelStatusesQuery,
    !!funnelId
  );
  const funnelStatusIds = useMemo(
    () => funnelStatuses.map((status) => status.id),
    [funnelStatuses]
  );
  const { aiStatusIds } = useAiWorkspaceStatusIdsByStatus(
    funnelStatusIds,
    funnelStatusIds.length > 0
  );
  const { downloadConversationTableExport } =
    useDownloadFunnelConversationTableExport();
  const { hasPermission: canManageFunnels } = useHasPermissions([
    FunnelPermission.Create,
    FunnelPermission.Update,
    FunnelPermission.Delete,
  ]);
  useCommunicationsRealtime({ funnelId, conversationId });

  useEffect(() => {
    if (!conversationExportFileId || !conversationExportRequestId) {
      return;
    }

    const fileId = conversationExportFileId;
    const requestId = conversationExportRequestId;

    const downloadExport = async () => {
      try {
        const response = await downloadConversationTableExport({
          fileId,
          requestId,
        });

        if (!response.downloadUrl) {
          return;
        }

        // Avoid popup blockers: async window.open calls are often blocked.
        window.location.assign(response.downloadUrl);
      } catch (error) {
        console.error('Failed to download conversation export:', error);
      } finally {
        void navigate({
          to: CommunicationsRoute.to,
          search: (prev) => {
            const {
              conversationExportFileId: _,
              conversationExportRequestId: __,
              ...rest
            } = prev as CommunicationsSearch;

            return rest;
          },
          replace: true,
        });
      }
    };

    void downloadExport();
  }, [
    conversationExportFileId,
    conversationExportRequestId,
    downloadConversationTableExport,
    navigate,
  ]);

  useEffect(() => {
    const omitFirstFunnelSelection =
      loadingFunnels || funnels.length === 0 || funnelId;
    if (omitFirstFunnelSelection) return;

    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => ({
        ...(prev as CommunicationsSearch),
        view: (prev as CommunicationsSearch).view,
        funnelId: funnels[0].id,
      }),
    });
  }, [loadingFunnels, funnels, funnelId, navigate]);

  const handleFunnelSelect = (id: number) => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => {
        const {
          conversationId: _,
          contactId: __,
          ...rest
        } = prev as CommunicationsSearch;

        return {
          ...rest,
          funnelId: id,
        };
      },
    });
  };

  const handleConversationSelect = (id: number) => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => {
        const { contactId: _, ...rest } = prev as CommunicationsSearch;

        return {
          ...rest,
          conversationId: id,
        };
      },
    });
  };

  const handleConversationClose = () => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => {
        const {
          conversationId: _,
          contactId: __,
          ...rest
        } = prev as CommunicationsSearch;

        return rest;
      },
    });
  };

  const handleOpenContactProfile = (id: number) => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => ({
        ...(prev as CommunicationsSearch),
        contactId: id,
      }),
    });
  };

  const handleCloseContactProfile = () => {
    void navigate({
      to: CommunicationsRoute.to,
      search: (prev) => {
        const { contactId: _, ...rest } = prev as CommunicationsSearch;

        return rest;
      },
    });
  };

  const handleOpenSettings = () => {
    void navigate({
      to: SettingsFunnelsRoute.to,
      search: (_prev) => {
        return settingsSearchSchema.parse({
          phoneVersion: 'v1',
        });
      },
    });
  };

  const getConversationListWidth = () => {
    if (!conversationId && !contactId) return 'w-full';
    if (conversationId && contactId) {
      return isWideSplitViewScreen ? 'w-[30%]' : 'w-0 hidden';
    }
    if (conversationId) {
      return isSplitViewScreen ? 'w-7/12' : 'w-0 hidden';
    }
    if (contactId) {
      return isSplitViewScreen ? 'w-7/12' : 'w-0 hidden';
    }

    // This case shouldn't be reached, but fallback to full width
    return 'w-full';
  };

  const getConversationDetailWidth = () => {
    if (!conversationId) return 'w-0 hidden';
    if (!contactId) return isSplitViewScreen ? 'w-5/12' : 'w-full';
    if (!isSplitViewScreen) return 'w-0 hidden';
    return isWideSplitViewScreen ? 'w-[42%]' : 'w-[65%]';
  };

  const getContactProfileWidth = () => {
    if (!contactId) return 'w-0 hidden';
    if (!conversationId) return isSplitViewScreen ? 'w-5/12' : 'w-full';
    if (!isSplitViewScreen) return 'w-full';
    return isWideSplitViewScreen ? 'w-[28%]' : 'w-[35%]';
  };

  if (loadingFunnels) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  if (funnels.length === 0) {
    return (
      <EmptyState
        // TODO: check when implementing i18n
        title="No hay embudos disponibles"
        description="Crea un embudo para comenzar"
        icon={<PiGearSix className="w-full h-full" />}
        placement="vertical"
        actionContent={
          canManageFunnels ? (
            <div className="flex items-center gap-2">
              <PiGearSix className="w-5 h-5" />
              {/* TODO: check when implementing i18n */}
              <Text size="small">Gestionar Embudos</Text>
            </div>
          ) : null
        }
        onAction={canManageFunnels ? handleOpenSettings : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-col h-full">
      {funnelId && (
        <ConversationPanel
          funnels={funnels}
          selectedFunnelId={funnelId}
          aiStatusIds={aiStatusIds}
          onFunnelSelect={handleFunnelSelect}
          onConversationSelect={handleConversationSelect}
          onOpenContactProfile={handleOpenContactProfile}
          selectedConversationId={conversationId}
          layout={{
            conversationListWidth: getConversationListWidth(),
            conversationDetailWidth: getConversationDetailWidth(),
            contactProfileWidth: getContactProfileWidth(),
          }}
        >
          {conversationId && (
            <div
              className={`${getConversationDetailWidth()} min-w-0 transition-all duration-200`}
            >
              <ConversationDetailPanel
                conversationId={conversationId}
                aiStatusIds={aiStatusIds}
                onOpenProfile={handleOpenContactProfile}
                onClose={handleConversationClose}
                showDesktopMetadata={!contactId}
              />
            </div>
          )}
          {contactId && (
            <div
              className={`${getContactProfileWidth()} min-w-0 transition-all duration-200`}
            >
              <ContactDetailPanel
                onClose={handleCloseContactProfile}
                contactId={contactId}
                conversationId={conversationId}
              />
            </div>
          )}
        </ConversationPanel>
      )}
    </div>
  );
};

export default CommunicationsPage;
